// src/services/userService.js
import { createApi } from "@reduxjs/toolkit/query/react";

import { allPagesOf } from "../allPages";
import { baseQueryWithReauth } from "../authorization/authService";

export const userApi = createApi({
  reducerPath: "userApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["User"],
  endpoints: (builder) => ({
    // Every page of the users: the page draws its table, filters and counts from them; it
    // showed the first 50 (#136).
    getUsers: builder.query({
      queryFn: allPagesOf((showAll = false) => ({
        url: "/user/list_by_role",
        params: { show_all: showAll },
      })),

      providesTags: (result) =>
        result
          ? [
              ...result.data.items.map(({ id }) => ({ type: "User", id })),
              { type: "User", id: "LIST" },
            ]
          : [{ type: "User", id: "LIST" }],
    }),

    getUserDetail: builder.query({
      query: (userId) => ({
        url: `/user/${userId}/detail`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "User", id }],
    }),

    updateUserStatus: builder.mutation({
      query: ({ userId, status }) => ({
        url: `/user/${userId}/status`,
        method: "PATCH",
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "User", id },
        { type: "UserAnalytics", id: "LIST" }, // Invalidate UserAnalytics cache
      ],
    }),

    updateUserProfile: builder.mutation({
      query: (userUpdateData) => ({
        url: "/user/me",
        method: "PUT",
        body: userUpdateData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "User", id },
        { type: "UserAnalytics", id: "LIST" }, // Invalidate UserAnalytics cache
      ],
    }),

    // F-014 — `getUserRolesWithStatus` query removed. Backend endpoint
    // `GET /role/{user_id}/list` was anonymous and enumerable; this
    // mutation was exported but had zero jsx consumers. The same data
    // ships inside `GET /user/{user_id}/detail` for admins, gated by
    // users_management + the tenant check.

    deactivateUser: builder.mutation({
      query: ({ user_id }) => ({
        url: `user/deactivate/me`,
        method: "POST",
        body: { user_id },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "User", id },
        { type: "UserAnalytics", id: "LIST" }, // Invalidate UserAnalytics cache
      ],
    }),

    confirmEmail: builder.mutation({
      query: (token) => ({
        url: `/user/confirm-email/${token}`,
        method: "POST", // Changed from GET to POST
      }),
      // if confirming your email should refresh any "User" lists, invalidate here:
      invalidatesTags: [{ type: "User", id: "LIST" }],
    }),

    resendConfirmation: builder.mutation({
      query: ({ email, captcha_id, captcha_input }) => ({
        url: "/user/resend-confirmation",
        method: "POST",
        body: { email, captcha_id, captcha_input },
      }),
    }),

    updateUserRoleStatus: builder.mutation({
      query: ({ userId, roleId, status }) => ({
        url: `user/${parseInt(userId)}/roles/${parseInt(roleId)}/status`,
        method: "PUT",
        body: { status },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "User", id },
        { type: "UserAnalytics", id: "LIST" }, // Invalidate UserAnalytics cache
      ],
    }),

    getUserAnalyticsStatuses: builder.query({
      query: () => ({
        url: "/user/analytics/statuses",
        method: "GET",
      }),
      providesTags: [{ type: "UserAnalytics", id: "LIST" }],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useUpdateUserStatusMutation,
  useGetUserDetailQuery,
  useUpdateUserProfileMutation,
  useDeactivateUserMutation,
  useUpdateUserRoleStatusMutation,
  useGetUserAnalyticsStatusesQuery,
  useConfirmEmailMutation,
  useResendConfirmationMutation,
} = userApi;
